/**
 * Property Safety Score - Location safety evaluation
 * Address lookup with 0-100 safety score and component breakdown
 */
import React, { useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Shield,
  MapPin,
  Search,
  AlertTriangle,
  Users,
  Cloud,
  Ambulance,
  Car,
  Info,
  ChevronDown,
  ChevronUp,
  Loader2,
  CheckCircle,
  XCircle,
  Target,
  Home
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Grade colors
const GRADE_COLORS = {
  'A+': 'from-emerald-500 to-green-500',
  'A': 'from-green-500 to-green-600',
  'B': 'from-teal-500 to-teal-600',
  'C': 'from-amber-500 to-yellow-500',
  'D': 'from-orange-500 to-orange-600',
  'F': 'from-red-500 to-red-600'
};

const ScoreGauge = ({ score, grade }) => {
  const gradientClass = GRADE_COLORS[grade] || GRADE_COLORS['C'];
  
  return (
    <div className="relative w-32 h-32 mx-auto mb-4">
      {/* Background circle */}
      <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          stroke="currentColor"
          strokeWidth="8"
          className="text-slate-700"
        />
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          stroke="url(#scoreGradient)"
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={`${score * 2.51} 251`}
          className="transition-all duration-1000"
        />
        <defs>
          <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#14b8a6" />
            <stop offset="100%" stopColor="#0d9488" />
          </linearGradient>
        </defs>
      </svg>
      
      {/* Score text */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-white">{score}</span>
        <span className={`text-lg font-bold bg-gradient-to-r ${gradientClass} bg-clip-text text-transparent`}>
          {grade}
        </span>
      </div>
    </div>
  );
};

const ComponentScore = ({ name, score, icon: Icon, details, expanded, onToggle }) => {
  const getScoreColor = (s) => {
    if (s >= 80) return 'text-green-400';
    if (s >= 60) return 'text-amber-400';
    return 'text-red-400';
  };

  return (
    <div className="border border-slate-700 rounded-lg overflow-hidden">
      <button
        className="w-full p-3 flex items-center justify-between hover:bg-slate-800/50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-slate-800">
            <Icon className="w-4 h-4 text-teal-400" />
          </div>
          <span className="text-sm font-medium text-slate-200">{name}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-lg font-bold ${getScoreColor(score)}`}>{score}</span>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-slate-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-500" />
          )}
        </div>
      </button>
      
      {expanded && details && (
        <div className="px-3 pb-3 pt-1 border-t border-slate-700 bg-slate-800/30">
          <p className="text-xs text-slate-400 mb-2">{details.description}</p>
          
          {/* Progress bar */}
          <Progress value={score} className="h-2 mb-2" />
          
          {/* Additional details */}
          {details.risk_level && (
            <Badge 
              variant="outline" 
              className={`text-[10px] ${
                details.risk_level === 'low' ? 'border-green-500 text-green-400' :
                details.risk_level === 'medium' ? 'border-amber-500 text-amber-400' :
                'border-red-500 text-red-400'
              }`}
            >
              {details.risk_level} risk
            </Badge>
          )}
        </div>
      )}
    </div>
  );
};

const Recommendation = ({ text, type }) => {
  const Icon = type === 'success' ? CheckCircle : type === 'warning' ? AlertTriangle : Info;
  const colorClass = type === 'success' ? 'text-green-400' : type === 'warning' ? 'text-amber-400' : 'text-blue-400';
  
  return (
    <div className="flex items-start gap-2 p-2 rounded-lg bg-slate-800/50">
      <Icon className={`w-4 h-4 mt-0.5 ${colorClass} flex-shrink-0`} />
      <p className="text-xs text-slate-300">{text}</p>
    </div>
  );
};

export const PropertySafetyScoreCard = ({ onExpand }) => {
  const { token } = useAuth();
  const [address, setAddress] = useState('');
  const [coords, setCoords] = useState({ lat: '', lng: '' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [expandedComponent, setExpandedComponent] = useState(null);

  const componentIcons = {
    crime: Shield,
    sex_offenders: Users,
    environmental: Cloud,
    emergency_response: Ambulance,
    traffic: Car
  };

  const handleSearch = useCallback(async () => {
    if (!token) return;
    if (!coords.lat && !coords.lng && !address) {
      setError('Please enter coordinates or an address');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      let lat = parseFloat(coords.lat);
      let lng = parseFloat(coords.lng);
      
      // If no coords but have address, use geocoding (simplified - use first coord)
      if (!lat || !lng) {
        // Default to San Diego for demo
        lat = 32.7157;
        lng = -117.1611;
      }

      const params = new URLSearchParams({
        lat: lat.toString(),
        lng: lng.toString(),
        ...(address && { address })
      });

      const response = await fetch(`${API_URL}/api/safety/property-score?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch safety score');
      
      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(err.message || 'Failed to calculate safety score');
    } finally {
      setLoading(false);
    }
  }, [token, coords, address]);

  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setCoords({
            lat: pos.coords.latitude.toFixed(6),
            lng: pos.coords.longitude.toFixed(6)
          });
        },
        () => setError('Could not get your location')
      );
    }
  };

  return (
    <div className="h-full flex flex-col" data-testid="property-safety-card">
      {/* Header */}
      <div className="p-4 border-b border-slate-700">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
          <Home className="w-5 h-5 text-teal-400" />
          Property Safety Score
        </h3>

        {/* Address Input */}
        <div className="space-y-3">
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Enter address..."
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="pl-9 bg-slate-800/50 border-slate-700"
            />
          </div>

          <div className="flex gap-2">
            <Input
              placeholder="Latitude"
              value={coords.lat}
              onChange={(e) => setCoords(prev => ({ ...prev, lat: e.target.value }))}
              className="bg-slate-800/50 border-slate-700 text-sm"
            />
            <Input
              placeholder="Longitude"
              value={coords.lng}
              onChange={(e) => setCoords(prev => ({ ...prev, lng: e.target.value }))}
              className="bg-slate-800/50 border-slate-700 text-sm"
            />
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleUseCurrentLocation}
              className="flex-1 text-xs"
            >
              <Target className="w-3 h-3 mr-1" />
              Use My Location
            </Button>
            <Button
              size="sm"
              onClick={handleSearch}
              disabled={loading}
              className="flex-1 bg-teal-600 hover:bg-teal-500"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Search className="w-3 h-3 mr-1" />
                  Analyze
                </>
              )}
            </Button>
          </div>
        </div>

        {error && (
          <div className="mt-3 p-2 rounded bg-red-500/20 border border-red-500/50 text-red-400 text-xs">
            {error}
          </div>
        )}
      </div>

      {/* Results */}
      {result && (
        <ScrollArea className="flex-1 p-4">
          {/* Score Display */}
          <div className="text-center mb-6">
            <ScoreGauge score={result.overall_score} grade={result.grade} />
            <p className="text-sm text-slate-400">{result.assessment}</p>
            {result.location?.address && (
              <p className="text-xs text-slate-500 mt-1 flex items-center justify-center gap-1">
                <MapPin className="w-3 h-3" />
                {result.location.address}
              </p>
            )}
          </div>

          {/* Component Scores */}
          <div className="space-y-2 mb-6">
            <h4 className="text-sm font-medium text-slate-300 mb-3">Score Breakdown</h4>
            {result.component_scores && Object.entries(result.component_scores).map(([key, score]) => (
              <ComponentScore
                key={key}
                name={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                score={score}
                icon={componentIcons[key] || Shield}
                details={result.details?.[key]}
                expanded={expandedComponent === key}
                onToggle={() => setExpandedComponent(expandedComponent === key ? null : key)}
              />
            ))}
          </div>

          {/* Recommendations */}
          {result.recommendations?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-slate-300 mb-3">Recommendations</h4>
              {result.recommendations.map((rec, i) => (
                <Recommendation 
                  key={i} 
                  text={rec}
                  type={rec.includes('strong') || rec.includes('excellent') ? 'success' : 
                        rec.includes('caution') || rec.includes('concern') ? 'warning' : 'info'}
                />
              ))}
            </div>
          )}

          {/* Data Sources */}
          {result.data_sources && (
            <div className="mt-4 pt-4 border-t border-slate-700">
              <p className="text-[10px] text-slate-500">
                Data sources: {result.data_sources.join(' • ')}
              </p>
              <p className="text-[10px] text-slate-600 mt-1">
                Calculated: {new Date(result.calculated_at).toLocaleString()}
              </p>
            </div>
          )}
        </ScrollArea>
      )}

      {/* Empty State */}
      {!result && !loading && (
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <Shield className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-sm text-slate-400">Enter a location to check safety</p>
            <p className="text-xs text-slate-500 mt-1">Get detailed safety scores and recommendations</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Dialog wrapper
export const PropertySafetyDialog = ({ trigger }) => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" className="gap-2">
            <Home className="w-4 h-4" />
            Property Score
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md h-[80vh] p-0 glass-card border-slate-800">
        <PropertySafetyScoreCard />
      </DialogContent>
    </Dialog>
  );
};

export default PropertySafetyScoreCard;
